import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import moment from "moment-timezone";

import { Button, Table, Space, Tag, Popconfirm, message, Empty, Divider, Spin, Input } from "antd";
import {
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    CloseOutlined,
    SearchOutlined,
    ReloadOutlined,
    WarningOutlined,
} from "@ant-design/icons";

const UserList = () => {
    const userLoginData = JSON.parse(localStorage.getItem("auth"));
    const { role } = useParams();
    const navigate = useNavigate();
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth } };
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState(null);
    const [usersDelete, setUsersDelete] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const value = { role: role };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.post("/api/user/getUsers", value, config);
            console.log(response.data.filter((user) => user.is_delete === 0));
            setUsers(response.data.filter((user) => user.is_delete === 0));
            setUsersDelete(response.data.filter((user) => user.is_delete === 1));
            console.log(response.data);
        } catch (error) {
            message.error("Failed to fetch users");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchUsers();
    }, [role]);

    const handleDelete = async (id) => {
        await axios.post("/api/user/deleteUser", { id }, config).then((response) => {
            message.success("ลบข้อมูลผู้ใช้เรียบร้อยแล้ว!");
        });
        fetchUsers();
    };

    const handleRecovery = async (id) => {
        await axios.post("/api/user/recoveryUser", { id }, config).then((response) => {
            message.success("กู้คืนข้อมูลผู้ใช้เรียบร้อยแล้ว!");
        });
        fetchUsers();
    };

    // ฟังก์ชันใหม่: ลบผู้ใช้ถาวร
    const handlePermanentDelete = async (id) => {
        try {
            await axios.post("/api/user/permanentDeleteUser", { id }, config);
            message.success("ลบผู้ใช้และข้อมูลที่เกี่ยวข้องทั้งหมดเรียบร้อยแล้ว!");
            fetchUsers();
        } catch (error) {
            message.error("เกิดข้อผิดพลาดในการลบข้อมูล");
            console.error(error);
        }
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`ค้นหา ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        ค้นหา
                    </Button>
                    <Button onClick={() => handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
                        รีเซ็ต
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];
            return recordValue ? recordValue.toString().toLowerCase().includes(value.toLowerCase()) : "";
        },
    });

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters, confirm) => {
        clearFilters();
        setSearchText("");
        confirm();
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "no",
            key: "no",
        },
        {
            title: "name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            ...getColumnSearchProps("name", "ชื่อ"),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            sorter: (a, b) => a.email.localeCompare(b.email),
            ...getColumnSearchProps("email", "อีเมล"),
        },
        {
            title: "Parent Organization",
            dataIndex: "parent_organization",
            key: "parent_organization",
            sorter: (a, b) => {
                const aValue = a.parent_organization || "";
                const bValue = b.parent_organization || "";
                return aValue.localeCompare(bValue);
            },
            ...getColumnSearchProps("parent_organization", "องค์กรหลัก"),
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
        },
        {
            title: "status",
            dataIndex: "is_active",
            key: "is_active",
            render: (_, { is_active }) => (
                <Tag color={is_active === 1 ? "green" : "red"}>{is_active === 1 ? `active` : `inactive`}</Tag>
            ),
        },

        {
            title: "Create Date",
            dataIndex: "date",
            key: "date",
            sorter: (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
        },
        {
            title: "Action",
            key: "action",
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button type="primary" onClick={() => navigate(`/users/${role}/edit/${record.id}`)}>
                        <EditOutlined />
                        แก้ไข
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this user?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger>
                            <DeleteOutlined />
                            ลบ
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const columnsDelete = [
        {
            title: "No.",
            dataIndex: "no",
            key: "no",
        },
        {
            title: "name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            ...getColumnSearchProps("name", "ชื่อ"),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            sorter: (a, b) => a.email.localeCompare(b.email),
            ...getColumnSearchProps("email", "อีเมล"),
        },
        {
            title: "Parent Organization",
            dataIndex: "parent_organization",
            key: "parent_organization",
            sorter: (a, b) => {
                const aValue = a.parent_organization || "";
                const bValue = b.parent_organization || "";
                return aValue.localeCompare(bValue);
            },
            ...getColumnSearchProps("parent_organization", "องค์กรหลัก"),
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
        },
        {
            title: "status",
            dataIndex: "is_active",
            key: "is_active",
            render: (_, { is_active }) => (
                <Tag color={is_active === 1 ? "green" : "red"}>{is_active === 1 ? `active` : `inactive`}</Tag>
            ),
        },

        {
            title: "Create Date",
            dataIndex: "date",
            key: "date",
            sorter: (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
        },
        {
            title: "Action",
            key: "action",
            width: 250,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => navigate(`/users/${role}/edit/${record.id}`)}
                        icon={<EditOutlined />}
                    >
                        แก้ไข
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to restore this user?"
                        onConfirm={() => handleRecovery(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button icon={<ReloadOutlined />} type="default">
                            กู้คืนผู้ใช้
                        </Button>
                    </Popconfirm>
                    <Popconfirm
                        title={
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                                    <WarningOutlined style={{ color: "#ff4d4f" }} /> คำเตือน!
                                </div>
                                <div>คุณแน่ใจหรือไม่ที่จะลบผู้ใช้นี้ถาวร?</div>
                                <div style={{ fontSize: "12px", color: "#666", marginTop: 4 }}>
                                    การดำเนินการนี้จะลบข้อมูลทั้งหมด รวมถึง:
                                </div>
                                <ul style={{ fontSize: "12px", color: "#666", marginLeft: 16, marginTop: 4 }}>
                                    <li>ใบประกาศและบทเรียนทั้งหมด (กรณีผู้สอน)</li>
                                    <li>คำถามและตัวเลือกทั้งหมด</li>
                                    <li>ประวัติการสอบทั้งหมด</li>
                                    <li>ใบประกาศนียบัตรทั้งหมด</li>
                                    <li>ข้อมูลการลงทะเบียนเรียน</li>
                                </ul>
                                <div style={{ fontSize: "12px", color: "#ff4d4f", marginTop: 8, fontWeight: "bold" }}>
                                    ⚠️ การกระทำนี้ไม่สามารถย้อนกลับได้!
                                </div>
                            </div>
                        }
                        onConfirm={() => handlePermanentDelete(record.id)}
                        okText="ยืนยันการลบถาวร"
                        cancelText="ยกเลิก"
                        okButtonProps={{ danger: true }}
                    >
                        <Button danger type="primary" icon={<DeleteOutlined />}>
                            ลบถาวร
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <>
                <Space align="baseline" style={{ marginBottom: "10px" }}>
                    <h2>{role === "teacher" ? "รายชื่อผู้สอน" : "รายชื่อผู้เรียน"}</h2>
                </Space>
                {users && users.length > 0 ? (
                    <>
                        <Table
                            loading={loading}
                            dataSource={
                                users.map((user, index) => ({
                                    key: index,
                                    no: index + 1,
                                    name: `${user.firstname} ${user.lastname}`,
                                    date: moment.tz(user.created_at, "Asia/Bangkok").format("LLL"),
                                    ...user,
                                })) ?? []
                            }
                            columns={columns}
                            pagination={{
                                defaultPageSize: 10,
                                total: users.length,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                            }}
                        />
                    </>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}

                {userLoginData.role === "super_admin" && (
                    <>
                        <Divider />
                        <Space align="baseline" style={{ marginBottom: "10px" }}>
                            <h2>{role === "teacher" ? "รายชื่อผู้สอนที่ถูกลบ" : "รายชื่อผู้เรียนที่ถูกลบ"}</h2>
                        </Space>
                        {usersDelete && usersDelete.length > 0 ? (
                            <>
                                <Table
                                    dataSource={
                                        usersDelete.map((user, index) => ({
                                            key: index,
                                            no: index + 1,
                                            name: `${user.firstname} ${user.lastname}`,
                                            date: moment.tz(user.created_at, "Asia/Bangkok").format("LLL"),
                                            ...user,
                                        })) ?? []
                                    }
                                    columns={columnsDelete}
                                    pagination={{
                                        defaultPageSize: 10,
                                        total: usersDelete.length,
                                        showSizeChanger: true,
                                        pageSizeOptions: ["10", "20", "50", "100"],
                                    }}
                                />
                            </>
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </>
                )}
            </>
        </>
    );
};

export default UserList;
